// Shadow of VLT 8.0.0-alpha.28 Navigation.tsx — enables the fat menu on wantai.
//
// WHY A SHADOW (not a data setting): VLT gates the fat menu on
// `has_fat_menu` read from the `voltolighttheme.header` inherit behavior, but
// nick does not provide that behavior (verified: it is absent from the Site /
// Languageroot / Page schemas on the wantai nick), so the data-flag path is a
// dead end. This is the same wall ekonomi hit (DR-2026-248 / plan 2026-07-13);
// the sanctioned fix is a Navigation shadow that removes the `hasFatMenu`
// gating so any top item WITH children renders the fat-menu panel.
//
// DELTAS vs upstream VLT Navigation.tsx: types dropped (.jsx); package-absolute
// doesNodeContainClick import; header-settings/form/`hasFatMenu` machinery
// removed (an item with children always renders the fat-menu branch; a childless
// item renders a plain NavItem); the `getNavigation` refetch effect kept
// verbatim. Markup + class names are byte-identical to upstream so VLT's CSS and
// a11y behaviour apply unchanged. Unlike ekonomi's shadow this does NOT group
// items — wantai renders the raw content-tree children (navDepth=3 in VLT), so
// "How the European Diagnostics Market Works" opens a panel with its country
// sub-pages, and the menu grows as the tree grows.
//
// Re-verify against upstream on any VLT bump.
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { NavLink } from 'react-router-dom';
import doesNodeContainClick from '@kitconcept/volto-light-theme/helpers/doesNodeContainClick';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';
import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clearSVG from '@plone/volto/icons/clear.svg';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';

const messages = defineMessages({
  closeMenu: { id: 'Close menu', defaultMessage: 'Close menu' },
  openFatMenu: { id: 'Open menu', defaultMessage: 'Open menu' },
});

const Navigation = ({ pathname }) => {
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(null);
  const [currentOpenIndex, setCurrentOpenIndex] = useState(null);
  const navigation = useRef(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const lang = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const items = useSelector((state) => state.navigation.items, shallowEqual);

  const closeMenu = () => {
    setDesktopMenuOpen(null);
    setCurrentOpenIndex(null);
  };

  // this function doesn't close the navigation when clicking the scrollbar
  const doesScrollbarContainClick = (event) => {
    const clickedVerticalScrollbar =
      event.clientX >= document.documentElement.clientWidth &&
      event.clientX <= window.innerWidth;
    const clickedHorizontalScrollbar =
      event.clientY >= document.documentElement.clientHeight &&
      event.clientY <= window.innerHeight;
    return clickedVerticalScrollbar || clickedHorizontalScrollbar;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navigation.current && doesNodeContainClick(navigation.current, event))
        return;
      if (doesScrollbarContainClick(event)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside, false);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  const isActive = (url) =>
    (url === '' && pathname === '/') || (url !== '' && pathname === url);

  const openMenu = (index) => {
    if (index === currentOpenIndex) {
      setDesktopMenuOpen(null);
      setCurrentOpenIndex(null);
    } else {
      setDesktopMenuOpen(index);
      setCurrentOpenIndex(index);
    }
  };

  // DR-2026-284 D-2: hovering a top-level item other than the open one dismisses
  // the fat menu — so moving onto a menuless item ("Items…"/"Deck") closes the
  // panel and clears the open item's active-underline, instead of leaving two
  // underlines and an open panel while the pointer is elsewhere. Click still
  // opens; hover never auto-opens. Entering the open item's own panel does NOT
  // close it: the panel is a child of that item's <li>, so no mouseEnter fires on
  // a different item and the `open !== index` guard holds.
  const dismissIfHoveringOther = (index) => {
    if (currentOpenIndex !== null && currentOpenIndex !== index) closeMenu();
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) closeMenu();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation"
      ref={navigation}
    >
      <div className={'computer large screen widescreen only'}>
        <ul className="desktop-menu">
          {items.map((item, index) => (
            <li
              key={item.url}
              onMouseEnter={() => dismissIfHoveringOther(index)}
            >
              {item.items && item.items.length > 0 ? (
                <>
                  <button
                    onClick={() => openMenu(index)}
                    className={cx('item', {
                      active:
                        desktopMenuOpen === index ||
                        (!desktopMenuOpen && pathname === item.url),
                    })}
                    aria-label={intl.formatMessage(messages.openFatMenu)}
                    aria-expanded={desktopMenuOpen === index}
                  >
                    {item.title}
                  </button>
                  <div className="submenu-wrapper">
                    <div
                      className={cx('submenu', {
                        active: desktopMenuOpen === index,
                      })}
                    >
                      <div className="submenu-inner">
                        <NavLink
                          to={item.url === '' ? '/' : item.url}
                          onClick={() => closeMenu()}
                          className="submenu-header"
                        >
                          <h2>{item.nav_title ?? item.title}</h2>
                        </NavLink>
                        <button
                          className="close"
                          onClick={closeMenu}
                          aria-label={intl.formatMessage(messages.closeMenu)}
                        >
                          <Icon name={clearSVG} size="48px" />
                        </button>
                        <ul>
                          {item.items.map((subitem) => (
                            <li className="subitem-wrapper" key={subitem.url}>
                              <NavLink
                                to={subitem.url}
                                onClick={() => closeMenu()}
                                className={cx({
                                  current: isActive(subitem.url),
                                })}
                              >
                                <span className="left-arrow">&#8212;</span>
                                <span>{subitem.nav_title || subitem.title}</span>
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <NavItem item={item} lang={lang} key={item.url} />
              )}
            </li>
          ))}
          {/* Static link to the selling deck. /en/deck is a nonContentRoute
              (DR-2026-261), so it is not among the content-driven nav items;
              append it as a plain nav item styled like a childless one.
              Language-aware: stays within the current languageroot's deck
              (/en/deck | /zh-cn/deck), label localized (DR-2026-264 zh-CN). */}
          {(() => {
            const lr =
              (pathname || '').split('/')[1] === 'zh-cn' ? 'zh-cn' : 'en';
            const deckUrl = `/${lr}/deck`;
            return (
              <li
                key="wantai-deck"
                onMouseEnter={() => dismissIfHoveringOther(-1)}
              >
                <NavLink
                  to={deckUrl}
                  className={cx('item', { active: pathname === deckUrl })}
                  onClick={closeMenu}
                >
                  {lr === 'zh-cn' ? '演示' : 'Deck'}
                </NavLink>
              </li>
            );
          })()}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
